import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Select from "react-select";

import customStyles from "../../../../assets/select/customStyles";
import SelectMultiple from "../../../CommonComponents/SelectMultiple";

const sortedIds = (ids) =>
  ids
    .map(Number)
    .filter(Number.isFinite)
    .sort((left, right) => left - right);

const sameIds = (left, right) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

const SmartSignSettingsTypes = forwardRef((props, ref) => {
  const { data, type: settingsType, isMulti = false, onSaved } = props;
  const jwt = localStorage.getItem("JWT");
  const link = localStorage.getItem("link");
  const [selectedIds, setSelectedIds] = useState([]);
  const [originalIds, setOriginalIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    setTypes(JSON.parse(localStorage.getItem("lotTypes") || "[]"));
  }, []);

  const options = useMemo(
    () =>
      types.map((type) => ({ value: Number(type.id), label: type.name })),
    [types],
  );

  const isDirty = () => !sameIds(selectedIds, originalIds);
  const reset = () => setSelectedIds(originalIds);

  const save = async () => {
    if (!isDirty() || saving || (!isMulti && !selectedIds.length)) return;

    const elementId = Number(data.id ?? data.Id ?? data.ids?.[0]);
    if (!Number.isFinite(elementId)) throw new Error("Invalid element id");

    setSaving(true);
    try {
      await axios.put(
        `${link}/api/${settingsType}/LotType?elementId=${elementId}`,
        selectedIds,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        },
      );

      const response = await axios.get(
        `${link}/api/${settingsType}/ById?id=${elementId}`,
        { headers: { Authorization: `Bearer ${jwt}` } },
      );
      const storedTypes = response.data?.lotTypes ?? response.data?.LotTypes ?? [];
      const storedIds = sortedIds(
        storedTypes.map((type) => type.id ?? type.Id),
      );

      if (!sameIds(storedIds, selectedIds)) {
        throw new Error("Lot types were not persisted by the backend");
      }

      setOriginalIds(storedIds);
      setSelectedIds(storedIds);
      if (onSaved) onSaved(response.data);
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ isDirty, save, reset }));

  useEffect(() => {
    const lotTypes = data?.lotTypes ?? data?.LotTypes;
    const fallbackTypeId =
      data?.countingAreaLotTypeId ??
      data?.CountingAreaLotTypeId ??
      data?.lotTypeId ??
      data?.LotTypeId ??
      data?.typeId ??
      data?.TypeId;
    const ids = sortedIds(
      Array.isArray(lotTypes)
        ? lotTypes.map((type) => type.id ?? type.Id)
        : fallbackTypeId === undefined || fallbackTypeId === null
          ? []
          : [fallbackTypeId],
    );
    const initialIds = isMulti ? ids : ids.slice(0, 1);

    setSelectedIds(initialIds);
    setOriginalIds(initialIds);
  }, [data, isMulti]);

  if (isMulti) {
    return (
      <SelectMultiple
        options={options}
        value={selectedIds}
        onChange={(ids) => setSelectedIds(sortedIds(ids))}
        compact
        openUpward
      />
    );
  }

  return (
    <Select
      className="select"
      styles={customStyles}
      isSearchable={false}
      value={
        options.find((option) => Number(option.value) === selectedIds[0]) ||
        null
      }
      onChange={(option) =>
        setSelectedIds(option ? [Number(option.value)] : [])
      }
      options={options}
      menuPlacement="auto"
      theme={(theme) => ({
        ...theme,
        borderRadius: 3,
        colors: { ...theme.colors, primary: "#20a0ff" },
      })}
    />
  );
});

export default SmartSignSettingsTypes;
